import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  doc, collection,   getDocs, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import { Button, Modal, ModalBanner } from './ui';
import { fmtMoney, findExchangeRate } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesBrokerPayment } from '../utils/ledger';
import { useSettings } from '../hooks/useSettings';
import type { SaleInvoice, PurchaseInvoice, Entity, PaymentStatus, BankAccount } from '../types';

// A broker deal is either a sale (broker paid by the seller) or a purchase
// (commission the buyer owes a broker) — same shape Brokers.tsx's own `Deal`
// type uses, duplicated here per this app's per-file-helper convention.
// A LineDeal is the per-line Broker1/Broker2 counterpart — one synthetic,
// computed-not-persisted row per distinct broker found on an invoice/sale's
// line items (mirrors Brokers.tsx's own LineDeal exactly; see that file for
// the full rationale). Bulk pay now covers both kinds — a header deal's
// payment updates the invoice's own brokerPaidUSD/brokerPaymentStatus; a
// line deal's payment is tagged with brokerEntityId instead and leaves
// those invoice-level fields alone (there's no single header broker to
// update), same distinction RecordBrokerPaymentModal.tsx's own
// `lineBrokerEntityId` prop makes for a single payment.
interface LineDeal {
  _source: 'sale' | 'purchase';
  _lineBroker: true;
  id: string;
  invoiceNo: string;
  partyId: string;
  totalAmountUSD: number;
  brokerEntityId: string;
  brokerCommissionPercent?: number;
  brokerCommissionUSD: number;
  brokerPaidUSD: number;
  brokerPaymentStatus: PaymentStatus;
}
type Deal =
  | (SaleInvoice     & { _source: 'sale'; _lineBroker?: false })
  | (PurchaseInvoice & { _source: 'purchase'; _lineBroker?: false })
  | LineDeal;

function isLineDeal(d: Deal): d is LineDeal {
  return d._lineBroker === true;
}
function dealPartyId(d: Deal): string {
  if (isLineDeal(d)) return d.partyId;
  return d._source === 'sale' ? d.toEntityId : d.fromEntityId;
}
// A line deal isn't unique by `id` alone — one invoice can produce several
// (one per broker on it) — so its key folds in `brokerEntityId` too. Same
// scheme as Brokers.tsx's own `dealKey`.
function dealKey(d: Deal): string {
  return isLineDeal(d) ? `${d._source}_${d.id}_${d.brokerEntityId}` : `${d._source}_${d.id}`;
}

function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
function todayISO() {
  return new Date().toISOString().split('T')[0];
}
// Invoice No is stored in full, but shown here as just the prefix + last 4
// digits to keep the row compact. Keeping the prefix matters — a bare
// last-4 truncation would collapse different prefixes into the same
// number, making them indistinguishable.
function lotNoDisplay(invoiceNo?: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
function commissionOutstanding(inv: { brokerCommissionUSD?: number; brokerPaidUSD?: number }): number {
  return Math.max(0, (inv.brokerCommissionUSD ?? 0) - (inv.brokerPaidUSD ?? 0));
}
interface Row {
  invoice: Deal;
  include: boolean;
  amount:  string;
}

interface Props {
  invoices: Deal[];   // pre-filtered to ones with commission outstanding > 0
  entities: Entity[];
  onClose:  (paidCount?: number) => void;
}

// ── Mini icons ────────────────────────────────────────────────────────────────

function IconCash() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="1.5" y="5" width="17" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 10h1M14 10h1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconBank() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="8" width="16" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 8l8-5 8 5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5 11.5v3.5M10 11.5v3.5M15 11.5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LotPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
// Settles commission on several SaleInvoices at once — same bank account and
// date for all, one payment doc + one linked BankTransaction written per
// invoice (for traceability), inside a single transaction so the account
// balance only moves by the combined total.

export default function BulkRecordBrokerPaymentModal({ invoices, entities, onClose }: Props) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const [accounts,      setAccounts]      = useState<BankAccount[]>([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [paidDate,      setPaidDate]      = useState(todayISO());
  const [referenceNo,   setReferenceNo]   = useState('');
  // One row per deal, grouped by broker (then invoice) so one broker's deals
  // sit together; built once when the form opens.
  const [rows,          setRows]          = useState<Row[]>(() => {
    const nameOf = (id?: string) => (id ? (entities.find(e => e.id === id)?.companyName ?? '') : '');
    return [...invoices]
      .sort((a, b) => nameOf(a.brokerEntityId).localeCompare(nameOf(b.brokerEntityId)) || (a.invoiceNo ?? '').localeCompare(b.invoiceNo ?? ''))
      .map(inv => ({ invoice: inv, include: true, amount: commissionOutstanding(inv).toFixed(2) }));
  });
  const [rate,          setRate]          = useState('');
  const [rateTouched,   setRateTouched]   = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');

  useEffect(() => {
    if (!business) return;
    const db = getClientDb();
    if (!db) return;
    getDocs(
      collection(db, 'bankAccounts'),
    ).then(snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount));
      const accs = all
        .filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setAccounts(accs);
      const def = accs.find(a => a.isDefault) ?? accs[0];
      if (def) setBankAccountId(def.id);
    }).catch(err => console.error('Failed to load bank accounts:', err));
  }, [business]);

  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  const selectedAccount = accounts.find(a => a.id === bankAccountId) ?? null;
  const currency        = selectedAccount?.currency ?? 'USD';
  const baseCurrency     = settings.currency;
  // Every deal here settles from the SAME account, so one shared rate
  // covers the whole batch — mirrors RecordBrokerPaymentModal.tsx's own
  // per-payment conversion, just applied once instead of per-row.
  const needsConversion = !!selectedAccount && selectedAccount.currency !== baseCurrency;
  const configuredRate = needsConversion ? findExchangeRate(settings.exchangeRates, baseCurrency, selectedAccount!.currency) : null;

  // The rate box follows the Dashboard rate until it's typed in; picking
  // another account lets the Dashboard rate take over again.
  const [rateFor, setRateFor] = useState(bankAccountId);
  if (rateFor !== bankAccountId) { setRateFor(bankAccountId); setRateTouched(false); }
  const rateValue = rateTouched || !configuredRate ? rate : String(configuredRate);
  const rateNum = needsConversion ? (parseFloat(rateValue) || 0) : 1;

  function setRowAmount(key: string, amount: string) {
    setRows(rs => rs.map(r => dealKey(r.invoice) === key ? { ...r, amount } : r));
  }
  function toggleRow(key: string) {
    setRows(rs => rs.map(r => dealKey(r.invoice) === key ? { ...r, include: !r.include } : r));
  }
  function toggleAll() {
    const allIn = rows.every(r => r.include);
    setRows(rs => rs.map(r => ({ ...r, include: !allIn })));
  }

  const included = rows.filter(r => r.include);
  const total = useMemo(
    () => included.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [included],
  );
  // The figure that actually moves in the account's own currency — total
  // itself when no conversion is needed, otherwise total × the shared rate.
  const bankTotal = needsConversion ? total * rateNum : total;
  const afterBalance = selectedAccount ? selectedAccount.currentBalance - bankTotal : null;

  // A row is overpaying if its own amount exceeds that invoice's own outstanding.
  const overpayingRows = included.filter(r => (parseFloat(r.amount) || 0) > commissionOutstanding(r.invoice) + 0.005);

  function validate(): string {
    if (!bankAccountId) return 'Select an account to pay from.';
    if (!paidDate) return 'Select a payment date.';
    if (needsConversion) {
      const r = parseFloat(rateValue);
      if (!rateValue || isNaN(r) || r <= 0) return `Enter the conversion rate to ${selectedAccount!.currency} — 1 ${baseCurrency} equals how many ${selectedAccount!.currency}.`;
    }
    if (included.length === 0) return 'Select at least one deal to pay.';
    if (included.some(r => !(parseFloat(r.amount) > 0))) return 'Enter a valid amount for every selected deal.';
    if (overpayingRows.length > 0) return 'One or more amounts exceed that deal’s outstanding commission. Please adjust before proceeding.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }
    const account = selectedAccount!;

    try {
      await runTransaction(db, async tx => {
        const accRef  = doc(db, 'bankAccounts', account.id);
        const accSnap = await tx.get(accRef);   // all reads before any writes
        if (!accSnap.exists()) throw new Error('Bank account no longer exists.');
        const currentBal = (accSnap.data() as BankAccount).currentBalance;

        const nowISO = new Date().toISOString();

        included.forEach(row => {
          const amt        = parseFloat(row.amount);
          const bankAmt    = needsConversion ? amt * rateNum : amt;
          const inv         = row.invoice;
          const coll        = inv._source === 'purchase' ? 'invoices' : 'sales';
          const brokerName  = entityName(inv.brokerEntityId);
          // A line deal has no single header-level broker on the invoice to
          // update — it's tagged on the payment doc instead (brokerEntityId),
          // the same distinction RecordBrokerPaymentModal.tsx's own
          // `lineBrokerEntityId` makes for a single payment.
          const lineBrokerEntityId = isLineDeal(inv) ? inv.brokerEntityId : undefined;

          const payRef = doc(collection(db, coll, inv.id, 'brokerPayments'));
          const invRef = doc(db, coll, inv.id);
          const txRef  = doc(collection(db, 'bankTransactions'));

          logActivity(tx, db, business!.id, user, {
            module: 'broker', action: 'create', recordId: inv.id, recordNo: inv.invoiceNo,
            partyId: inv.brokerEntityId, amountUSD: amt, note: `${account.name} · Pay Selected`,
          });
          tx.set(payRef, stripUndef({
            saleId:     inv._source === 'sale'     ? inv.id : undefined,
            purchaseId: inv._source === 'purchase' ? inv.id : undefined,
            businessId: business!.id,
            brokerEntityId: lineBrokerEntityId,
            amount: amt, currency: baseCurrency, paidDate,
            bankAmount:   needsConversion ? bankAmt : undefined,
            bankCurrency: needsConversion ? account.currency : undefined,
            exchangeRate: needsConversion ? rateNum : undefined,
            bankAccountId: account.id, bankAccountName: account.name,
            bankTransactionId: txRef.id,
            referenceNo: referenceNo.trim() || undefined,
            notes: `Bulk payment (${included.length} deals)`,
            createdAt: nowISO, createdBy: user!.id,
          }));

          if (!lineBrokerEntityId) {
            const alreadyPaid = inv.brokerPaidUSD ?? 0;
            const newPaid     = alreadyPaid + amt;
            const status: PaymentStatus =
              newPaid >= (inv.brokerCommissionUSD ?? 0) - 0.005 ? 'paid'
              : newPaid > 0 ? 'partial'
              : 'unpaid';
            tx.update(invRef, { brokerPaidUSD: newPaid, brokerPaymentStatus: status, updatedAt: nowISO });
          }

          tx.set(txRef, stripUndef({
            businessId: business!.id, accountId: account.id, accountName: account.name,
            currency: account.currency, direction: 'out', amount: bankAmt, date: paidDate,
            description: `${brokerName || 'Broker commission'} — (${lotNoDisplay(inv.invoiceNo)})`,
            referenceType: 'broker_payment', referenceId: inv.id,
            referenceNo: referenceNo.trim() || undefined,
            notes: `Bulk payment (${included.length} deals)`,
            createdAt: nowISO, createdBy: user!.id,
          }));

          // Ledger (additive) — one entry per settled deal, base currency
          // (same as the commission itself; only the bank side above converts).
          if (inv.brokerEntityId) {
            postEntry(tx, db, buildEntry({
              businessId: business!.id,
              sourceType: 'broker_payment',
              sourceId: payRef.id,
              sourceCollection: `${coll}/${inv.id}/brokerPayments`,
              date: paidDate,
              currency: baseCurrency,
              createdBy: user!.id,
              note: `Broker payout — ${brokerName || 'broker'} (${lotNoDisplay(inv.invoiceNo)})`,
              lines: linesBrokerPayment(amt, inv.brokerEntityId, account.id),
            }), entryId('broker_payment', payRef.id));
          }
        });

        tx.update(accRef, { currentBalance: currentBal - bankTotal });
      });

      onClose(included.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record payments.');
    }
    setSaving(false);
  }

  const allIncluded = rows.length > 0 && rows.every(r => r.include);
  const someIncluded = included.length > 0 && !allIncluded;
  const owedSelected = included.reduce((s, r) => s + commissionOutstanding(r.invoice), 0);
  const stillOwed    = Math.max(0, owedSelected - total);
  const brokerIds    = [...new Set(rows.map(r => r.invoice.brokerEntityId).filter(Boolean))] as string[];
  const oneBroker    = brokerIds.length === 1 ? entityName(brokerIds[0]) : '';

  const TH: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-2)',
    padding: '7px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-3)',
    textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left',
    whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)',
  };
  const TD: React.CSSProperties = { padding: '7px 10px', fontSize: 12, color: 'var(--text-2)', borderBottom: '1px solid var(--border-2)', whiteSpace: 'nowrap' };
  const TF: React.CSSProperties = {
    position: 'sticky', bottom: 0, background: 'var(--surface-2)', padding: '8px 10px',
    fontSize: 12, fontWeight: 700, color: 'var(--text-1)', borderTop: '1px solid var(--border)',
  };
  const sumRow = (label: string, val: ReactNode, color = 'var(--text-1)', last = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', borderBottom: last ? 'none' : '1px solid var(--border-2)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700, color }}>{val}</span>
    </div>
  );

  return (
    <Modal
      open
      onClose={() => onClose()}
      hideHead
      size="2xl"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => onClose()} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={included.length === 0 || overpayingRows.length > 0}>
              Pay {money(total)}
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={oneBroker || 'Pay Brokers'}
        eyebrow={oneBroker ? 'Bulk Broker Payment' : `Bulk Broker Payment · ${brokerIds.length} brokers`}
        right={
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>Deals</div>
            <div style={{ fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>{included.length} of {rows.length}</div>
          </div>
        }
        onClose={() => onClose()}
      />

      {/* ── Top: account + details on the left, summary on the right ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 24, alignItems: 'start', marginBottom: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className="form-section-label" style={{ marginBottom: 10 }}>Pay From</div>
            {accounts.length === 0 ? (
              <div className="msg msg-info" style={{ margin: 0, fontSize: 12 }}>No bank accounts found. Add one in Bank › Accounts first.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                {accounts.map(acc => {
                  const isSelected = bankAccountId === acc.id;
                  return (
                    <button key={acc.id} type="button" onClick={() => setBankAccountId(acc.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 'var(--r-md)',
                      border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                      background: isSelected ? 'var(--primary-light)' : 'var(--surface)',
                      cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.14s, background 0.14s',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: acc.type === 'cash' ? 'var(--success-bg)' : 'var(--primary-light)',
                        color: acc.type === 'cash' ? 'var(--success)' : 'var(--primary)',
                      }}>
                        {acc.type === 'cash' ? <IconCash /> : <IconBank />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{acc.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{fmtMoney(acc.currentBalance, acc.currency)} available</div>
                      </div>
                      {isSelected && (
                        <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary)', color: '#fff' }}>
                          <IconCheck />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div className="form-section-label" style={{ marginBottom: 10 }}>Payment Details</div>
            <div className={needsConversion ? 'form-row-4' : 'form-row-2'} style={{ gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Payment Date</label>
                <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Reference No.</label>
                <input value={referenceNo} onChange={e => setReferenceNo(e.target.value)} placeholder="Same for all deals" style={{ fontFamily: 'var(--font-num)' }} />
              </div>
              {needsConversion && (<>
                <div className="form-group" style={{ margin: 0 }} title={configuredRate ? 'Filled from the Dashboard exchange rates. Change it if the bank used another rate.' : 'Enter today\'s rate.'}>
                  <label>{`Rate (1 ${baseCurrency} = ${selectedAccount!.currency})`}</label>
                  <input className="no-spinner" type="number" step="0.0001" min="0.0001" value={rateValue}
                    onChange={e => { setRate(e.target.value); setRateTouched(true); }} placeholder="0.0000" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{`Total in ${selectedAccount!.currency}`}</label>
                  <input value={bankTotal > 0 ? bankTotal.toFixed(2) : ''} placeholder="0.00" disabled />
                </div>
              </>)}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-2)', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Summary
          </div>
          {sumRow('Deals selected', `${included.length} of ${rows.length}`)}
          {sumRow('Owed on them', money(owedSelected), 'var(--danger)')}
          {sumRow('Paying now', money(total), 'var(--success)')}
          {sumRow('Still owed after', money(stillOwed), stillOwed > 0.005 ? 'var(--danger)' : 'var(--text-3)', afterBalance === null)}
          {afterBalance !== null && sumRow(`${selectedAccount!.name} after`, fmtMoney(afterBalance, currency), afterBalance < 0 ? 'var(--danger)' : 'var(--text-2)', true)}
        </div>
      </div>

      {/* ── Deals ── */}
      <div className="form-section-label" style={{ marginBottom: 10 }}>Deals</div>
      <div style={{ border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'auto', maxHeight: 340, overscrollBehavior: 'contain' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: 36, textAlign: 'center' }}>
                <input type="checkbox" checked={allIncluded} ref={el => { if (el) el.indeterminate = someIncluded; }} onChange={toggleAll} title="Select all" />
              </th>
              <th style={{ ...TH, width: 36, textAlign: 'right' }}>#</th>
              <th style={TH}>Broker</th>
              <th style={{ ...TH, width: 60 }}>Type</th>
              <th style={{ ...TH, width: 110 }}>Invoice No</th>
              <th style={TH}>Party</th>
              <th style={{ ...TH, width: 120, textAlign: 'right' }}>Owed</th>
              <th style={{ ...TH, width: 150, textAlign: 'right' }}>Pay Now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const owed = commissionOutstanding(row.invoice);
              const amt  = parseFloat(row.amount) || 0;
              const isOverpaying = row.include && amt > owed + 0.005;
              const key = dealKey(row.invoice);
              const purchase = row.invoice._source === 'purchase';
              return (
                <tr key={key} style={{ opacity: row.include ? 1 : 0.5 }}>
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <input type="checkbox" checked={row.include} onChange={() => toggleRow(key)} />
                  </td>
                  <td style={{ ...TD, textAlign: 'right', color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{i + 1}</td>
                  <td style={{ ...TD, color: 'var(--text-1)', fontWeight: 600 }}>{entityName(row.invoice.brokerEntityId)}</td>
                  <td style={TD}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                      background: purchase ? 'var(--warning-bg)' : 'var(--primary-light)',
                      color: purchase ? '#c2410c' : 'var(--primary)',
                    }}>{purchase ? 'Buy' : 'Sale'}</span>
                  </td>
                  <td style={TD} title={row.invoice.invoiceNo || undefined}><LotPill>{lotNoDisplay(row.invoice.invoiceNo)}</LotPill></td>
                  <td style={{ ...TD, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={entityName(dealPartyId(row.invoice))}>{entityName(dealPartyId(row.invoice))}</td>
                  <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(owed)}</td>
                  <td style={{ ...TD, padding: '4px 8px', textAlign: 'right' }}>
                    <input
                      type="number" step="0.01" min="0" disabled={!row.include} className="no-spinner"
                      value={row.amount}
                      onChange={e => setRowAmount(key, e.target.value)}
                      title={isOverpaying ? `More than the ${money(owed)} owed` : undefined}
                      style={{
                        width: 130, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12,
                        padding: '5px 8px', borderRadius: 6,
                        border: `1.5px solid ${isOverpaying ? 'var(--danger)' : 'var(--border)'}`,
                        background: isOverpaying ? 'color-mix(in srgb, var(--danger) 8%, var(--surface))' : 'var(--surface)',
                        color: 'var(--text-1)',
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6} style={TF}>Total · {included.length} deal{included.length !== 1 ? 's' : ''}{brokerIds.length > 1 ? ` · ${brokerIds.length} brokers` : ''}</td>
              <td style={{ ...TF, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(owedSelected)}</td>
              <td style={{ ...TF, textAlign: 'right', fontFamily: 'var(--font-num)', paddingRight: 18 }}>{money(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {overpayingRows.length > 0 && (
        <p className="form-hint" style={{ color: 'var(--danger)', fontWeight: 600, marginTop: 8 }}>
          {overpayingRows.length} deal{overpayingRows.length !== 1 ? 's are' : ' is'} set to pay more than is owed. Lower the red amount{overpayingRows.length !== 1 ? 's' : ''}.
        </p>
      )}
    </Modal>
  );
}
